import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncData } from '@/hooks';
import { workflowsApi } from '@/api/workflows';
import type { WorkflowInstance, WorkflowProcess } from '@/types/workflows';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import solutionMindset from '@/assets/images/undraw_solution-mindset_5xp7.svg';
import { KebabMenu } from '@/components/ui/Menu';
import { formatDateTime } from '@/utils/format';
import { stepProgress } from './inbox-grouping';
import { InstanceDetailModal, instanceStatusBadge } from './InstanceDetailModal';
import styles from '../AdminWorkflowsPage.module.css';

/**
 * The full register: every case, past and pending, filterable by status and
 * process. This is the audit view — where an administrator answers "what
 * happened to the bursar's waiver" and "what has been stuck for a week" —
 * so rows open the same detail modal the inbox uses, with the same chain
 * picture, rather than a second dialect of the truth.
 *
 * Filtering is SERVER-SIDE (the query params the instances endpoint takes),
 * not a client sieve: the register can outgrow one payload, and a filter that
 * only sifts the first page quietly lies about everything past it.
 */

const NS = 'admin.workflows';

const STATUS_FILTERS = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;

interface InstancesTabProps {
  institutionId: string;
  processes: WorkflowProcess[];
  processTitles: Map<string, string>;
}

export function InstancesTab({ institutionId, processes, processTitles }: InstancesTabProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const [status, setStatus] = useState('');
  const [processKey, setProcessKey] = useState('');
  const [detail, setDetail] = useState<WorkflowInstance | null>(null);

  const { state, reload } = useAsyncData<WorkflowInstance[]>(
    `wf-instances-${institutionId}-${status}-${processKey}`,
    () =>
      workflowsApi.member.instances(institutionId, {
        status: status || undefined,
        processKey: processKey || undefined,
      }),
    !!institutionId,
  );

  const processTitle = (key: string) => processTitles.get(key) ?? key;
  const filtered = status !== '' || processKey !== '';

  const clearFilters = () => {
    setStatus('');
    setProcessKey('');
  };

  return (
    <Card>
      <div className={styles.registerToolbar}>
        <div className={styles.registerFilters}>
          <Select
            aria-label={t(`${NS}.instances.filterStatus`)}
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: t(`${NS}.instances.allStatuses`) },
              ...STATUS_FILTERS.map((value) => ({
                value,
                label: t(`${NS}.instanceStatus.${value}`),
              })),
            ]}
          />
          <Select
            aria-label={t(`${NS}.instances.filterProcess`)}
            value={processKey}
            onChange={setProcessKey}
            searchable
            options={[
              { value: '', label: t(`${NS}.instances.allProcesses`) },
              ...processes.map((process) => ({ value: process.key, label: process.title })),
            ]}
          />
        </div>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        empty={
          <EmptyState
            illustration={solutionMindset}
            title={t(`${NS}.instances.empty`)}
            description={t(`${NS}.instances.emptyBody`)}
            action={
              filtered && (
                <Button onClick={clearFilters}>{t(`${NS}.instances.clearFilters`)}</Button>
              )
            }
          />
        }
      >
        {(data) => (
          <div className={styles.tableScroll}>
            <table className={styles.registerTable}>
              <thead>
                <tr>
                  <th>{t(`${NS}.instances.colSubject`)}</th>
                  <th>{t(`${NS}.instances.colProcess`)}</th>
                  <th>{t(`${NS}.instances.colStatus`)}</th>
                  <th>{t(`${NS}.instances.colStep`)}</th>
                  <th>{t(`${NS}.instances.colInitiator`)}</th>
                  <th>{t(`${NS}.instances.colCreated`)}</th>
                  <th>
                    <span className={styles.srOnly}>{t('common.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((instance) => {
                  const progress = stepProgress(instance);
                  return (
                    <tr key={instance.id}>
                      <td>
                        {/* The subject opens the detail — a real button, so the
                            row works from the keyboard, not a clickable <tr>. */}
                        <button
                          type="button"
                          className={styles.subjectLink}
                          onClick={() => setDetail(instance)}
                        >
                          {instance.subjectLabel}
                        </button>
                      </td>
                      <td>{processTitle(instance.processKey)}</td>
                      <td>
                        {instanceStatusBadge(
                          instance.status,
                          t(`${NS}.instanceStatus.${instance.status}`),
                        )}
                      </td>
                      <td>
                        {instance.status === 'PENDING' && progress.total > 0
                          ? t(`${NS}.inbox.stepOf`, { current: progress.current, total: progress.total })
                          : '—'}
                      </td>
                      <td>{instance.initiatedByName}</td>
                      <td>{formatDateTime(instance.createdAt, locale)}</td>
                      <td className={styles.rowActions}>
                        <KebabMenu
                          ariaLabel={t(`${NS}.instances.rowActions`, { subject: instance.subjectLabel })}
                          items={[
                            {
                              label: t(`${NS}.inbox.view`),
                              onClick: () => setDetail(instance),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>

      <InstanceDetailModal
        instance={detail}
        institutionId={institutionId}
        processTitle={detail ? processTitle(detail.processKey) : ''}
        onClose={() => setDetail(null)}
        onChanged={() => {
          setDetail(null);
          reload();
        }}
      />
    </Card>
  );
}
